/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2022. All Rights Reserved.
 #*
 #*===================================================================
 */

import { createReducer, on } from '@ngrx/store';

import {
  AgingReport,
  BillCollectionItem,
  BillCollectionReport,
  ClientDocument,
  Invoice,
  INVOICE_STATUSES,
} from '@core/models';
import { AccountingActions } from '../actions';

export const ACCOUNTING_STORE_KEY = 'accounting';

export interface AccountingModuleState {
  agingReport: AgingReport | null;
  agingInvoices: BillCollectionItem[] | null;
  billCollectionReport: BillCollectionReport;
  invoiceDetails: { invoice: Invoice | null; orderTermsDocument: ClientDocument | null };
  loading: boolean;
  processing: boolean;
  errorMessage: string;
}

export const initialState: AccountingModuleState = {
  agingReport: null,
  agingInvoices: null,
  billCollectionReport: { items: null, total: 0 },
  invoiceDetails: { invoice: null, orderTermsDocument: null },
  loading: false,
  processing: false,
  errorMessage: '',
};

export const accountingReducer = createReducer(
  initialState,

  on(
    AccountingActions.loadAgingReport,
    AccountingActions.loadAgingInvoices,
    AccountingActions.loadBillCollection,
    AccountingActions.loadInvoiceDetails,
    AccountingActions.addInvoiceItem,
    AccountingActions.deleteInvoiceItem,
    (state) => ({
      ...state,
      loading: true,
    })
  ),

  on(
    AccountingActions.flagInvoice,
    AccountingActions.updateInvoiceFlag,
    AccountingActions.deleteInvoiceFlag,
    AccountingActions.setPaymentInfo,
    (state) => ({
      ...state,
      processing: true,
    })
  ),

  on(AccountingActions.loadAgingReportSuccess, (state, { report }) => ({
    ...state,
    agingReport: report,
    loading: false,
  })),

  on(AccountingActions.loadAgingInvoicesSuccess, (state, { invoices }) => ({
    ...state,
    agingInvoices: invoices,
    loading: false,
  })),

  on(AccountingActions.loadBillCollectionSuccess, (state, { report }) => ({
    ...state,
    billCollectionReport: report,
    loading: false,
  })),

  on(AccountingActions.loadInvoiceDetailsSuccess, (state, { invoice }) => ({
    ...state,
    invoiceDetails: { ...state.invoiceDetails, invoice },
    loading: false,
  })),

  on(AccountingActions.flagInvoiceSuccess, AccountingActions.updateInvoiceFlagSuccess, (state, { invoiceFlag }) => ({
    ...state,
    agingInvoices: (state.agingInvoices || [])?.map((i) =>
      i.invoiceId === invoiceFlag.invoiceId ? { ...i, invoiceFlag } : i
    ),
    billCollectionReport: {
      ...state.billCollectionReport,
      items: state.billCollectionReport.items
        ? state.billCollectionReport.items.map((i) =>
            i.invoiceId === invoiceFlag.invoiceId ? { ...i, invoiceFlag } : i
          )
        : null,
    },
    invoiceDetails: state.invoiceDetails.invoice
      ? {
          ...state.invoiceDetails,
          invoice: {
            ...state.invoiceDetails.invoice,
            invoiceFlag,
          },
        }
      : state.invoiceDetails,
    processing: false,
  })),

  on(AccountingActions.deleteInvoiceFlagSuccess, (state, { invoiceFlag, flagedInvoicesFilter }) => ({
    ...state,
    agingInvoices: (state.agingInvoices || [])?.map((i) =>
      i.invoiceId === invoiceFlag.invoiceId ? { ...i, invoiceFlag: null } : i
    ),
    billCollectionReport: {
      ...state.billCollectionReport,
      items: state.billCollectionReport.items
        ? flagedInvoicesFilter
          ? state.billCollectionReport.items.filter((i) => i.invoiceId !== invoiceFlag.invoiceId)
          : state.billCollectionReport.items.map((i) =>
              i.invoiceId === invoiceFlag.invoiceId ? { ...i, invoiceFlag: null } : i
            )
        : null,
      total: flagedInvoicesFilter ? state.billCollectionReport.total - 1 : state.billCollectionReport.total,
    },
    invoiceDetails: state.invoiceDetails.invoice
      ? {
          ...state.invoiceDetails,
          invoice: {
            ...state.invoiceDetails.invoice,
            invoiceFlag: null,
          },
        }
      : state.invoiceDetails,
    processing: false,
  })),

  on(AccountingActions.setPaymentInfoSuccess, (state, { invoice }) => ({
    ...state,
    invoiceDetails: state.invoiceDetails.invoice
      ? {
          ...state.invoiceDetails,
          invoice: {
            ...state.invoiceDetails.invoice,
            status: invoice.status,
            invoicePaymentInfo: { ...invoice.invoicePaymentInfo },
          },
        }
      : state.invoiceDetails,
    billCollectionReport: {
      ...state.billCollectionReport,
      items: state.billCollectionReport.items
        ? state.billCollectionReport.items.map((i) =>
            i.invoiceId === invoice.id ? { ...i, status: invoice.status, paymentInfo: invoice.invoicePaymentInfo } : i
          )
        : null,
    },
    agingInvoices: (state.agingInvoices || [])?.map((i) =>
      i.invoiceId === invoice.id ? { ...i, status: invoice.status, paymentInfo: invoice.invoicePaymentInfo } : i
    ),
    processing: false,
  })),

  on(AccountingActions.loadOrderTermsDocumentSuccess, (state, { orderTermsDocument }) => ({
    ...state,
    invoiceDetails: state.invoiceDetails.invoice
      ? {
          ...state.invoiceDetails,
          orderTermsDocument,
        }
      : state.invoiceDetails,
  })),

  on(
    AccountingActions.addInvoiceItemSuccess,
    AccountingActions.deleteInvoiceItemSuccess,
    (state, { invoice, billCollectionItem }) => ({
      ...state,
      invoiceDetails: state.invoiceDetails.invoice
        ? {
            ...state.invoiceDetails,
            invoice,
          }
        : state.invoiceDetails,
      billCollectionReport: {
        ...state.billCollectionReport,
        items: state.billCollectionReport.items
          ? state.billCollectionReport.items.map((item) => {
              const keepPastDueStatus =
                item.status === INVOICE_STATUSES.PastDue && billCollectionItem.status !== INVOICE_STATUSES.ToBeBilled;

              return item.invoiceId === billCollectionItem.invoiceId
                ? {
                    ...billCollectionItem,

                    status: keepPastDueStatus ? billCollectionItem.status : item.status,
                  }
                : item;
            })
          : null,
      },
      agingInvoices: (state.agingInvoices || []).map((item) =>
        item.invoiceId === billCollectionItem.invoiceId ? billCollectionItem : item
      ),
      loading: false,
    })
  ),

  on(
    AccountingActions.loadAgingInvoicesFailure,
    AccountingActions.loadAgingReportFailure,
    AccountingActions.loadBillCollectionFailure,
    AccountingActions.addInvoiceItemFailure,
    AccountingActions.loadInvoiceDetailsFailure,
    AccountingActions.deleteInvoiceItemFailure,
    (state, { errorMessage }) => ({
      ...state,
      loading: false,
      errorMessage,
    })
  ),

  on(
    AccountingActions.flagInvoiceFailure,
    AccountingActions.updateInvoiceFlagFailure,
    AccountingActions.deleteInvoiceFlagFailure,
    AccountingActions.setPaymentInfoFailure,
    (state, { errorMessage }) => ({
      ...state,
      processing: false,
      errorMessage,
    })
  )
);
